import React from 'react'
import useStore from 'state/knovStore'
import { isMobile } from 'lib/device'
import {
    resolveOnePanelGesturePrewarmTarget,
    shouldPrewarmOnePanelTransition,
} from 'lib/onePanelTransitionPrewarm'
import { warmPanelMediaForTransition } from 'lib/panelMediaWarmCache'
import queryClient from 'api/queryClient'

// Swipe speed constants
export const SWIPE_SPEED_FAST = 300
export const SWIPE_SPEED_SLOW = 500

const swiperRef = React.createRef()
const ONE_PANEL_MEDIA_PREWARM_DEADLINE_MS = 48
// A thread still loading would arrive as a skeleton and fill in mid-slide; give its
// fetch a short head start so it lands with content. Bounded so navigation never waits
// on the network for long.
const ONE_PANEL_QUEST_WAIT_MS = 250
const ONE_PANEL_PREWARM_MAX_PREPARE_MS = 320
let onePanelPrewarmToken = 0
let onePanelPrewarmFallback = null

function clearOnePanelPrewarm(targetIndex) {
    const state = useStore.getState()
    if (targetIndex != null && state.onePanelPrewarmIndex !== targetIndex) return
    if (state.onePanelPrewarmIndex == null && !state.onePanelPrewarmVisible) return
    state.set({ onePanelPrewarmIndex: null, onePanelPrewarmVisible: false })
}

function cancelScheduledOnePanelPrewarm() {
    onePanelPrewarmToken += 1
    if (onePanelPrewarmFallback != null) {
        window.clearTimeout(onePanelPrewarmFallback)
        onePanelPrewarmFallback = null
    }
}

function panelPrewarmScope(targetIndex) {
    const targetPanel = useStore.getState().panels.state[targetIndex]
    const targetPanelId = String(targetPanel?.panelId || '')
    if (!targetPanelId || typeof document === 'undefined') return null

    return [...document.querySelectorAll('[data-testid="panel-scroll-container"]')].find(
        node => node.getAttribute('data-panel-id') === targetPanelId,
    )
}

function performSwiperSlideTo(swiperIndex, speed) {
    try {
        const result = swiperRef.current.slideTo(swiperIndex, speed)
        if (result && typeof result.catch === 'function') {
            result.catch(err => {
                console.error('Swiper slideTo promise rejected:', err)
            })
        }
    } catch (err) {
        console.error('Swiper slideTo failed:', err)
    }
}

function beginOnePanelTransition(token, targetIndex, speed) {
    if (token !== onePanelPrewarmToken) return false

    // Invalidate every outstanding preparation callback before starting Swiper. The
    // active-index callback normally clears the marker; this fallback also covers a
    // rejected/no-op slide without dropping the user's navigation request.
    onePanelPrewarmToken += 1
    if (onePanelPrewarmFallback != null) {
        window.clearTimeout(onePanelPrewarmFallback)
    }
    onePanelPrewarmFallback = window.setTimeout(() => {
        clearOnePanelPrewarm(targetIndex)
        onePanelPrewarmFallback = null
    }, speed + 250)
    performSwiperSlideTo(targetIndex, speed)
    return true
}

function waitForPanelQuest(targetIndex, timeoutMs) {
    const questId = useStore.getState().panels.state[targetIndex]?.filter?.questId
    if (!questId) return Promise.resolve()
    const queryKey = ['quest', String(questId)]
    // A child thread is seeded from its parent post (no posts yet) and then fetched in
    // full under the same key; the seed alone still renders as a skeleton. A complete
    // thread never waits on a background refetch.
    const settled = () => {
        const state = queryClient.getQueryState(queryKey)
        if (state?.status === 'error') return true
        /** @type {import('types/quests').Quest | null | undefined} */
        const quest = /** @type {any} */ (state?.data)
        const complete =
            Boolean(quest?.sorted_answer_ids?.length) &&
            quest.__knovAnswerHydrationScope !== 'stream-preview'
        return complete || (Boolean(quest) && state.fetchStatus !== 'fetching')
    }
    if (settled()) return Promise.resolve()

    return new Promise(resolve => {
        let unsubscribe = () => {}
        const done = () => {
            window.clearTimeout(timeout)
            unsubscribe()
            resolve()
        }
        const timeout = window.setTimeout(done, timeoutMs)
        unsubscribe = queryClient.getQueryCache().subscribe(() => {
            if (settled()) done()
        })
    })
}

function scheduleOnePanelPrewarmedSlide(targetIndex, speed) {
    cancelScheduledOnePanelPrewarm()
    const token = onePanelPrewarmToken
    const state = useStore.getState()
    state.set({ onePanelPrewarmIndex: targetIndex, onePanelPrewarmVisible: false })
    onePanelPrewarmFallback = window.setTimeout(() => {
        beginOnePanelTransition(token, targetIndex, speed)
    }, ONE_PANEL_PREWARM_MAX_PREPARE_MS)

    try {
        swiperRef.current.virtual?.update?.(true)
    } catch {}

    // Two frames guarantee one complete hidden paint opportunity after the destination
    // subtree mounts (with its thread, when that arrives in time). Media decoding gets a
    // small additional deadline but can never hold navigation indefinitely.
    void waitForPanelQuest(targetIndex, ONE_PANEL_QUEST_WAIT_MS).then(() => {
        if (token !== onePanelPrewarmToken) return
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(async () => {
                if (token !== onePanelPrewarmToken) return

                try {
                    await warmPanelMediaForTransition(
                        panelPrewarmScope(targetIndex),
                        ONE_PANEL_MEDIA_PREWARM_DEADLINE_MS,
                    )
                } catch {}
                if (token !== onePanelPrewarmToken) return
                if (!swiperRef.current || swiperRef.current.destroyed) {
                    clearOnePanelPrewarm(targetIndex)
                    return
                }
                beginOnePanelTransition(token, targetIndex, speed)
            })
        })
    })
}

// TODO - improve swiperApi so that all swiper actions run through this api
export function getSwiperRef() {
    return swiperRef
}

export function getNumPanels() {
    return swiperRef?.current?.slides?.length || 0
}

export function getLeftIndex() {
    return swiperRef?.current?.activeIndex || 0
}

export function getCenterIndex() {
    const numPanels = useStore.getState().numPanels
    return isMobile || numPanels === 1
        ? swiperRef?.current?.activeIndex || 0
        : (swiperRef?.current?.activeIndex || 0) + 1
}

export function getRightIndex() {
    const numPanels = useStore.getState().numPanels
    return isMobile || numPanels === 1
        ? swiperRef?.current?.activeIndex || 0
        : (swiperRef?.current?.activeIndex || 0) + 2
}

export function isPanelOneOffLeft(panelId) {
    const panels = useStore.getState().panels
    const panelIx = panels.getIndexById(panelId)
    return panelIx === getLeftIndex() - 1
}

export function isPanelOneOffRight(panelId) {
    const panels = useStore.getState().panels
    const panelIx = panels.getIndexById(panelId)
    return panelIx === getRightIndex() + 1
}

export function isPanelLeft(panelId) {
    const panels = useStore.getState().panels
    const panelIx = panels.getIndexById(panelId)
    return panelIx === getLeftIndex()
}

export function isPanelCenter(panelId) {
    const panels = useStore.getState().panels
    const panelIx = panels.getIndexById(panelId)
    return panelIx === getCenterIndex()
}

export function isPanelRight(panelId) {
    const panels = useStore.getState().panels
    const panelIx = panels.getIndexById(panelId)
    return panelIx === getRightIndex()
}

export function getLeftPanel() {
    const panelState = useStore.getState().panels.state
    return panelState[getLeftIndex()]
}

export function getCenterPanel() {
    const panelState = useStore.getState().panels.state
    return panelState[getCenterIndex()]
}

export function getRightPanel() {
    const panelState = useStore.getState().panels.state
    return panelState[getRightIndex()]
}

export function slideTo(index, speed) {
    //console.log('slideTo', index, speed === 0 ? 'instant' : 'slide')
    const numPanels = useStore.getState().numPanels
    const onePanel = numPanels === 1

    // If speed is not explicitly provided, use default speeds
    if (speed === undefined) {
        // Use slower speed in onePanel mode, fast otherwise
        speed = onePanel && !isMobile ? SWIPE_SPEED_SLOW : SWIPE_SPEED_FAST
    }

    // Guard against calling slideTo on invalid/destroyed Swiper instance
    if (!swiperRef?.current || swiperRef.current.destroyed) {
        return
    }

    const swiperIndex = isMobile || onePanel ? index : index - 1
    const targetPanel = useStore.getState().panels.state[index]
    const shouldPrewarm = shouldPrewarmOnePanelTransition({
        currentIndex: swiperRef.current.activeIndex,
        isMobile,
        numPanels,
        speed,
        targetEmpty: targetPanel?.empty,
        targetExists: Boolean(targetPanel),
        targetIndex: swiperIndex,
    })

    if (shouldPrewarm) {
        scheduleOnePanelPrewarmedSlide(swiperIndex, speed)
        return
    }

    cancelScheduledOnePanelPrewarm()
    clearOnePanelPrewarm()
    performSwiperSlideTo(swiperIndex, speed)
}

export function completeOnePanelPrewarm() {
    cancelScheduledOnePanelPrewarm()
    clearOnePanelPrewarm()
}

export function prewarmOnePanelGesture(swiper, direction = swiper?.swipeDirection) {
    const state = useStore.getState()
    const targetIndex = resolveOnePanelGesturePrewarmTarget({
        currentIndex: swiper?.activeIndex ?? 0,
        direction,
        isMobile,
        numPanels: state.numPanels,
        panelCount: state.panels.state.length,
    })
    const targetPanel = targetIndex == null ? null : state.panels.state[targetIndex]

    if (targetIndex == null || !targetPanel || targetPanel.empty) {
        if (state.onePanelPrewarmVisible) {
            cancelScheduledOnePanelPrewarm()
            clearOnePanelPrewarm()
        }
        return
    }
    if (state.onePanelPrewarmIndex === targetIndex && state.onePanelPrewarmVisible) return

    cancelScheduledOnePanelPrewarm()
    state.set({ onePanelPrewarmIndex: targetIndex, onePanelPrewarmVisible: true })
    try {
        swiper.virtual?.update?.(true)
    } catch {}
}

export function finishOnePanelGesturePrewarm() {
    const state = useStore.getState()
    if (!state.onePanelPrewarmVisible || state.onePanelPrewarmIndex == null) return

    const targetIndex = state.onePanelPrewarmIndex
    cancelScheduledOnePanelPrewarm()
    onePanelPrewarmFallback = window.setTimeout(() => {
        clearOnePanelPrewarm(targetIndex)
        onePanelPrewarmFallback = null
    }, SWIPE_SPEED_SLOW + 250)
}

export function slidePrev(offset = 1, speed) {
    //console.log('swiper api slide right to index', getCenterIndex() - offset)
    slideTo(getCenterIndex() - offset, speed)
}

export function slideNext(offset = 1, speed) {
    //console.log('swiper api slide left to index', getCenterIndex() + offset)
    slideTo(getCenterIndex() + offset, speed)
}

export function getPanelsToLeft(index) {
    const panels = useStore.getState().panels.state
    return panels.slice(0, index)
}

export function getPanelsToRight(index) {
    const panels = useStore.getState().panels.state
    return panels.slice(index + 1)
}
